/* eslint-disable no-useless-escape */
import * as React from 'react';
import {
    Modal,
    Button,
    Grid,
    XFilledIcon,
    ProgressBar
} from 'react-unity';
import './RuleModal.css';
import TextAreaField from '../../../common/form-controls/TextareaField';
import ToggleField from '../../../common/form-controls/ToggleField';
import TextInputField from '../../../common/form-controls/TextInputField';
import { AbstractRulesForm, IRuleStateForm, RulesFormProps } from './AbstractRulesForm';
import { RuleAction } from '../../../../models/enums/DNDR/RuleAction';
import { RuleRegion } from '../../../../models/enums/DNDR/RuleRegion';
import { VirtualNetwork } from '../../../../models/entities/AzureResources/VirtualNetwork';
import SelectField from '../../../common/form-controls/SelectField';
import MultipleSelectField from '../../../common/form-controls/MultipleSelectField';
import { RuleOperation } from '../../../../models/enums/DNDR/RuleOperation';
import { DoNotDecryptRequestsDirection } from '../../../../models/enums/DNDR/DoNotDecryptRequestsDirection';
import { Address4, Address6 } from 'ip-address';
import { TrafficDirection } from '../../../../models/enums/DNDR/TrafficDirection';
import { Option } from '../../../../utils/Option';

interface RuleModalSubToSubProps extends RulesFormProps{
    subscriptionRequestName: string;
}




interface SubToSubRuleStateForm extends IRuleStateForm {
    name: string;
    trafficDirection: TrafficDirection;
}
interface RuleModalSubToSubState {
    stateForm: SubToSubRuleStateForm;
    options: Option[]
}

export default class RuleModalSubToSub extends AbstractRulesForm<
    RuleModalSubToSubProps,
    RuleModalSubToSubState
> {

    constructor(props: RuleModalSubToSubProps) {
        super(props);
        this.state = {
            stateForm: this.props.isFromHistory ? this.getRuleCopy() : this.getDefaultForm(),
            options: null
        };

        this.initFormModel();
    }
    
    isURLValid = (url) => /^(((http(s)?:\/\/.)?(www\.)?)|(\*\.)?)[a-zA-Z0-9]+[-a-zA-Z0-9@:%._\+~#=]{1,256}\.[a-z]{2,6}\b([-a-zA-Z0-9@:%_\+.~#?&//=]*)/.test(url);

    initFormModel() {
        super.initFormModel();
        this.formModel.addField('name', {
            getValue: () => this.state.stateForm.name,
            validation: {
                required: true,
                rules:
                    [
                        {
                            assert: () => this.state.stateForm.operation !== RuleOperation.Add
                                || this.state.stateForm.name?.length <= 24,
                            message: 'Name cannot be longer than 24 characters.',
                        },
                        {
                            assert: () => !this.state.stateForm.name?.includes(' '),
                            message: 'Name cannot contain spaces.',
                        },
                        {
                            assert: () => this.state.stateForm.operation !== RuleOperation.Add
                            || /^[a-zA-Z](?:[a-zA-Z_-]*[a-zA-Z])?$/.test(this.state.stateForm.name ?? ''),
                            message: 'Only letters (uppercase and lowercase), dashes, underscores are allowed. No trailing or beginning dashes or underscores.',
                        }
                    ]
            },
        });	

        const numberOfDestinationItemsValidation = {
            assert: () => {
                const numberOfIPs = this.state.stateForm.destinationIPs?.split(';').filter(s => s !== '').length ?? 0;
                const numberOfURLs = this.state.stateForm.destinationURLs?.split(';').filter(s => s !== '').length ?? 0;
                const numberOfDestinationItems = numberOfURLs + numberOfIPs;
                const maxNumberOfDestinationItems = 50;
                return numberOfDestinationItems <= maxNumberOfDestinationItems;
            },
            message: "Total Destination URLs and IPs can't be greater than 50"
        }

        this.formModel.addField('trafficDirection', {
			getValue: () => this.state.stateForm.trafficDirection,
		});

        this.formModel.addField('destinationURLs', {
            getValue: () => this.parseMultipleValues(this.state.stateForm.destinationURLs),
            validation: {
                required: () => !(this.state.stateForm.destinationIPs?.trim().length > 0),
                rules: [
                    {
                        assert: () => !this.hasEmptyValues(this.state.stateForm.destinationURLs),
                        message: 'Empty values are not allowed.'
                    },
                    {
                        assert: () => !(this.state.stateForm.destinationURLs.includes('https://')
                        ||this.state.stateForm.destinationURLs.includes('http://')),
                        message: 'URL cannot have "http" or "https"'
                    },
                    {
                        assert: (value) => {
                            const urls = this.parseMultipleValues(value)?.split(';');
                            return urls?.every(url => this.isURLValid(url));
                        },
                        message: (value) => {
                            const invalidAddress = this.parseMultipleValues(value)?.split(';').find(url => !this.isURLValid(url));
                            return `${invalidAddress} is an invalid URL.`;
                        }
                    },
                    numberOfDestinationItemsValidation
                ]
            }
        });

        // Destination can be satisfied by an IP (from completed POR rules) instead of a URL.
        this.formModel.addField('destinationIPs', {
            getValue: () => this.parseMultipleValues(this.state.stateForm.destinationIPs),
            validation: {
                required: () => !(this.state.stateForm.destinationURLs?.trim().length > 0),
                rules: [
                    numberOfDestinationItemsValidation
                ]
            }
        });

    }

    // eslint-disable-next-line class-methods-use-this
    getDefaultFormState() {
        return {
            operation: RuleOperation.Add,
            name: '',
            //destinationURLs: ''
        };
    }

    getStateFormHandlers() {
        const handlers = super.getStateFormHandlers();

        // Selecting a source IP resets its dependent destination selections.
        handlers.sourceIPs = {
            ...handlers.sourceIPs,
            onChange: (event) => {
                this.handleStateFormChange('sourceIPs', event.target.value);
                this.handleStateFormChange('destinationPorts', '');
                this.handleStateFormChange('destinationIPs', '');
            }
        };

        // Destination IP and URL are mutually exclusive; selecting one clears the other.
        // Neither clears the port, since IP/URL options depend on the selected port.
        handlers.destinationIPs = {
            ...handlers.destinationIPs,
            onChange: (event) => {
                this.handleStateFormChange('destinationIPs', event.target.value);
                
            }
        };

        return handlers;
    }

    getRegionOptions(): RuleRegion[] {
        var SubToSubRegions = new RuleRegion().listByDirection(DoNotDecryptRequestsDirection.SubscriptionToSubscription, this.props.validVirtualNetworks);
        const wwSubscriptionsNames = ['APPS_SharedSvcs_WW_DEV', 'APPS_SharedSvcs_WW_LAB', 'APPS_SharedSvcs_WW_PRD'];
        return wwSubscriptionsNames.includes(this.props.subscriptionRequestName) ? SubToSubRegions : SubToSubRegions.filter(region => region !== RuleRegion.WW);
    }

    getFilteredMappings() {
        if (!this.props.completedPORRules?.hasCompletedPORRules || !this.props.completedPORRules?.ruleMappings) {
            return [];
        }
        // ToInternet POR rules have Sub trafficDirection
        return this.props.completedPORRules.ruleMappings
            .filter(m => m.trafficDirection == 'Sub');
    }

    getSourceIPOptions() {
        if (this.props.completedPORRules?.hasCompletedPORRules) {
            const mappings = this.getFilteredMappings();
            const ips = [...new Set(mappings.map(m => m.sourceIP))];
            return ips.map(ip => ({ value: ip, text: ip }));
        }

        const subnets = this.props.validVirtualNetworks.flatMap(vNet => vNet.properties.subnets);
        const addressSpaces = subnets.flatMap(subnet => subnet.properties.addressPrefix).filter(a => a != null);
        return addressSpaces.map(ip => ({ value: ip, text: ip }));
    }

    getDestinationURLOptions() {
        if (this.props.completedPORRules?.hasCompletedPORRules) {
            const mappings = this.getFilteredMappings();
            const selectedSrcIP = this.state.stateForm.sourceIPs?.trim();
            const selectedDestPort = this.state.stateForm.destinationPorts?.trim();
            if (!selectedSrcIP || !selectedDestPort) return [];

            const urls = [...new Set(
                mappings
                    .filter(m => m.sourceIP === selectedSrcIP).filter(m => m.destinationPort === selectedDestPort)
                    .map(m => m.destinationURL)
            )];
            return urls.map(url => ({ value: url, text: url }));
        }
        return [];
    }

    getDestinationIPOptions() {
        if (this.props.completedPORRules?.hasCompletedPORRules) {
            const mappings = this.getFilteredMappings();
            const selectedSrcIP = this.state.stateForm.sourceIPs?.trim();
            const selectedDestPort = this.state.stateForm.destinationPorts?.trim();
            if (!selectedSrcIP || !selectedDestPort) return [];

            const ips = [...new Set(
                mappings
                    .filter(m => m.sourceIP === selectedSrcIP).filter(m => m.destinationPort === selectedDestPort)
                    .map(m => m.destinationIP)
                    .filter(ip => ip != null)
            )];
            return ips.map(ip => ({ value: ip, text: ip }));
        }
        return [];
    }

    getDestinationPortOptions() {
        if (this.props.completedPORRules?.hasCompletedPORRules) {
            const mappings = this.getFilteredMappings();
            const selectedSrcIP = this.state.stateForm.sourceIPs?.trim();
            if (!selectedSrcIP) return [];

            const ports = [...new Set(
                mappings
                    .filter(m => m.sourceIP === selectedSrcIP)
                    .map(m => m.destinationPort)
            )];
            return ports.map(port => ({ value: port, text: port }));
        }
        return [];
    }

    loadIPsValidations(direction) {}

    render() {
        return (
            <Modal show={this.props.visible}>
                <Modal.Window className="ruleModal">
                    <Modal.Window.Header>
                        <Modal.Window.Header.Title>
                            {this.props.isReadOnly ? 'View Rule' : !this.isModalForNewRule() ? 'Edit Rule' : 'New Rule'}
                        </Modal.Window.Header.Title>
                        <Modal.Window.Header.CloseButton onClick={this.props.onClose}>
                            <XFilledIcon size='small' />
                        </Modal.Window.Header.CloseButton>
                    </Modal.Window.Header>
                    <Modal.Window.Body>
                        { !this.isModalForNewRule() &&
                            <ToggleField
                                {...this.stateFormHandler().operation}
                                label="Operation"
                                disabled={this.props.isReadOnly}
                            />
                        }
                        <Grid variant="2-up">
                            <Grid.Item>
                                <TextInputField
                                    label="Rule Name?????????"
                                    value={this.state.stateForm.name}
                                    validation={this.formModel.fields.name.validation}
                                    onChange={
                                        (event) => {
                                            this.handleStateFormChange('name', event.target.value);
                                        }
                                    }								
                                    disabled={this.state.stateForm.operation?.name !== RuleOperation.Add.name || !this.formCanBeEdited()}
                                />
                            </Grid.Item>
                            <Grid.Item>
                                <SelectField
                                    {...this.stateFormHandler().region}
                                    options={this.getRegionOptions()}
                                    disabled={this.state.stateForm.operation?.name !== RuleOperation.Add.name || !this.formCanBeEdited()}
                                />
                            </Grid.Item>
                        </Grid>
                        <>
                            <Grid variant="2-up">
                                <Grid.Item>
                                </Grid.Item>
                                <Grid.Item /> 
                            </Grid>
                            <Grid variant="2-up">
                                <Grid.Item>
                                {!this.formCanBeEdited() ?
                                    <TextAreaField
                                        label="Source (IP)"
                                        value={this.stateFormHandler().sourceIPs.value}
                                        disabled={true}
                                    /> :
                                    this.state.options ?
                                    <ProgressBar
                                        label='Loading form...'
                                        className="em-u-margin-top-half"
                                        indeterminate
                                        hideValueLabel
                                    /> :										
                                        <SelectField
                                            {...this.stateFormHandler().sourceIPs}
                                            options={this.getSourceIPOptions()}
                                            label="Source (IP)"
                                            disabled={!this.formCanBeEdited()}
                                        />
                                }
                                </Grid.Item>
                                {/* <Grid.Item>
                                    <TextAreaField
                                        {...this.stateFormHandler().destinationIPs}
                                        label="Destination (IPs / IP range / CIDR)"
                                        disabled={!this.formCanBeEdited()}
                                    />
                                </Grid.Item> */}
                            </Grid>
                            {this.formCanBeEdited() ?
                                <>
                                <Grid variant="2-up">
                                    <Grid.Item>
                                        <SelectField
                                            {...this.stateFormHandler().destinationPorts}
                                            options={this.getDestinationPortOptions()}
                                            label="Destination Port"
                                            disabled={!this.formCanBeEdited() || !this.state.stateForm.sourceIPs}
                                        />
                                    </Grid.Item>
                                    <Grid.Item>
                                        <SelectField
                                            {...this.stateFormHandler().destinationIPs}
                                            options={this.getDestinationIPOptions()}
                                            label="Destination IP"
                                            disabled={!this.formCanBeEdited() || !this.state.stateForm.sourceIPs || !this.state.stateForm.destinationPorts}
                                        />
                                    </Grid.Item>
                                </Grid>
                                <Grid variant="2-up">
                                    <Grid.Item>
                                        <SelectField
                                            {...this.stateFormHandler().destinationURLs}
                                            options={this.getDestinationURLOptions()}
                                            label="Destination URLs"
                                            disabled={!this.formCanBeEdited() || !this.state.stateForm.sourceIPs || !this.state.stateForm.destinationPorts}
                                        />
                                    </Grid.Item>
                                    <Grid.Item />
                                </Grid>
                                </>
                                :
                                <>
                                <Grid variant="2-up">
                                    <Grid.Item>
                                        <TextAreaField
                                            label="Destination Port"
                                            value={this.stateFormHandler().destinationPorts.value}
                                            disabled={true}
                                        />
                                    </Grid.Item>
                                    <Grid.Item>
                                        <TextAreaField
                                            label="Destination IP"
                                            value={this.stateFormHandler().destinationIPs.value}
                                            disabled={true}
                                        />
                                    </Grid.Item>
                                </Grid>
                                <Grid variant="2-up">
                                    <Grid.Item>
                                        <TextAreaField
                                            label="Destination URLs"
                                            value={this.stateFormHandler().destinationURLs.value}
                                            disabled={true}
                                        />
                                    </Grid.Item>
                                    <Grid.Item />
                                </Grid>
                                </>
                            }
                            <Grid variant="2-up">
                                
                            </Grid>
                            <Grid variant="2-up">
                                <Grid.Item>
                                    <TextAreaField
                                        {...this.stateFormHandler().description}
                                        label="Description"
                                        disabled={!this.formCanBeEdited()}
                                    />
                                </Grid.Item>
                                {/*<Grid.Item>*/}
                                {/*	<TextAreaField*/}
                                {/*		label="Destination (URLs)"*/}
                                {/*		value={this.state.stateForm.destinationURLs}*/}
                                {/*		validation={this.formModel.fields.destinationURLs.validation}*/}
                                {/*		onChange={*/}
                                {/*			(event) => {*/}
                                {/*				this.handleStateFormChange('destinationURLs', event.target.value);*/}
                                {/*			}*/}
                                {/*		}*/}
                                {/*		note='Multiple values are allowed, separated by semicolons.'*/}
                                {/*		disabled={!this.formCanBeEdited()}*/}
                                {/*	/>*/}
                                {/*</Grid.Item>*/}
                            </Grid>
                        </>
                    </Modal.Window.Body>
                    { !this.props.isReadOnly &&
                        <Modal.Window.Footer>
                            <Grid variant="2-up">
                                <Grid.Item>
                                    <Button
                                        variant="primary"
                                        disabled={!this.formModel.isValid() && this.state.stateForm.operation.name !== RuleOperation.Remove.name}						
                                        onClick={this.handleRequest}
                                    >
                                        {this.props.editingRule ? 'Confirm' : 'Create'}
                                    </Button>
                                </Grid.Item>
                                <Grid.Item>
                                    <Button
                                        variant="secondary"
                                        onClick={this.handleCancel}
                                    >
                                        Cancel
                                    </Button>
                                </Grid.Item>
                            </Grid>
                        </Modal.Window.Footer>
                    }

                </Modal.Window>
            </Modal>
        );
    }
}